import {EventEmitter, Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, filter, map, mergeMap, Observable, of, take, throwError} from 'rxjs';
import {FileUploadStatus} from '../../model/file-upload-status';
import {environment} from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {HvbEncryptedUpload} from './HvbEncryptedUpload';
import {BinaryData} from '../../model/proposal/binary-data';
import {selectUserRoles} from '../../store/auth/selectors/auth.selectors';
import {Roles} from '../../enum/roles';
import {Store} from '@ngrx/store';
import SimpleCrypto, {PlainData} from 'simple-crypto-js';
import {MatSnackBar} from '@angular/material/snack-bar';
import {catchError, switchMap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {EXCEL_TEMPLATE_FILENAME} from '../../constants/question-options';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {

  private readonly fileUploadUrl = `${environment.backend}/v1/files`;
  fileUploadStatus$ = new BehaviorSubject<FileUploadStatus[]>([]);

  private fileUploadStatus: FileUploadStatus[] = [];

  private readonly assetFile = 'assets/img/form/';

  private readonly icons = {
    'pdf': this.assetFile + 'file.svg',
    'mp4': this.assetFile + 'media.svg',
    'unknown': this.assetFile + 'upload.svg',
  };

  emailSent: EventEmitter<void> = new EventEmitter();

  constructor(private readonly http: HttpClient, public store: Store, private readonly snackBar: MatSnackBar, private readonly translate: TranslateService) {
  }

  public async uploadFile(file: File, fileUploadStatus: FileUploadStatus) {
    fileUploadStatus.fileType = file.type;
    const internalId: string = fileUploadStatus.internalId;
    this.fileUploadStatus.push(fileUploadStatus);
    this.fileUploadStatus$.next(this.fileUploadStatus);

    let hvbEncryptedUpload = new HvbEncryptedUpload(
      file,
      {
        http: this.http,
        baseUrl: this.fileUploadUrl,
        readChunkSize: 500000,
        onProcessUpdate: (uploadProgress) => {
          this.fileUploadStatus.forEach(fus => {
            if (fus.internalId === fileUploadStatus.internalId) {
              fus.progress = uploadProgress;
            }
          });
        },
        onSuccess: (fileId: string, fileSecret: string) => {
          this.fileUploadStatus.forEach(value => {
            if (value.internalId === internalId) {
              value.progress = 100;
              // Sets id from backend upload -> This is carried along to the database as the id of the resulting BinaryData object
              // set fileSecret which is returned from the backend
              value.id = fileId;
              value.fileSecret = fileSecret
            }
          });
          this.fileUploadStatus$.next(this.fileUploadStatus);
        },
        onError: () => {
          // how to handle file upload error?
          // option 1, show loading spinner
          this.fileUploadStatus.forEach(fus => {
            if (fus.internalId === fileUploadStatus.internalId) {
              fus.progress = 0;
            }
          });
          // option 2. delete file from upload list
          // this.fileUploadStatus = this.fileUploadStatus.filter(fus => fus.internalId != fileUploadStatus.internalId);

          this.fileUploadStatus$.next(this.fileUploadStatus);
        }
      }
    );
    this.fileUploadStatus$.pipe(filter(fileUploadStatusList => !fileUploadStatusList.includes(fileUploadStatus) || fileUploadStatus.progress == 100), take(1)).subscribe({
      complete: () => hvbEncryptedUpload.stop()
    });
    try {
      await hvbEncryptedUpload.start();
    } catch (err) {
      fileUploadStatus.isError = true;
      throw err;
    }
  }

  public clearFileFromFileUploadStatusArray(internalId: string) {
    this.fileUploadStatus = this.fileUploadStatus.filter(fileUploadStatus => fileUploadStatus.internalId !== internalId);
    this.fileUploadStatus$.next(this.fileUploadStatus);
  }

  public addPreUploadedFile(preUploadedFile: FileUploadStatus) {
    this.fileUploadStatus.push(preUploadedFile);
    this.fileUploadStatus$.next(this.fileUploadStatus);
  }

  public getIconSourceForFilename(fileName: string) {
    let name = fileName.split('.');
    let key = name[name.length - 1];
    if (key in this.icons) {
      return this.icons[key];
    }
    return this.icons['unknown'];
  }

  // this is new refactored deleteFileUpload which does file deletion on UI and also file metadata deletion on backend
  // the exact file will be deleted on proposal/offer submit
  // this refactoring was done because there was a bug in which file would get deleted but proposal/offer edit cancelled, which would
  // result in proposal/offer pointing to a file that does not exist anymore
  public deleteFileUpload(id: string): Observable<void> {
    return of(null).pipe(
      switchMap(() =>
        this.store.select(selectUserRoles).pipe(take(1))  // get roles once
      ),
      switchMap(roles => {
        if (roles.includes(Roles.ROLE_ASSOCIATION_ADMIN)) {
          return this.http.delete(this.fileUploadUrl + '/binary/' + id);
        }
        return of(null); // skip binary delete if role is not association admin
      }),
      map(() => {
        this.fileUploadStatus = this.fileUploadStatus.filter(fileUploadStatus => fileUploadStatus.id !== id);
        this.fileUploadStatus$.next(this.fileUploadStatus);
      }),
      catchError(error => throwError(() => error))
    );
  }


  // combines new file logic and also old one for backward compatibility
  public downloadFile(id: string, fileSecret: string, fileName: string, fileType: string): Observable<any> {
    return combineLatest([
      this.http.get<number[]>(this.fileUploadUrl + '/' + id + '/chunk-sizes'),
      this.http.get(`${this.fileUploadUrl}/${id}/download?fileSecret=${encodeURIComponent(fileSecret)}`, {
        responseType: 'blob',
        observe: 'response',
      })
    ]).pipe(
      mergeMap(async ([chunkSizes, response]) => {
        const encryptedFileString = await this.blobToText(response.body);
        const isDeprecatedUpload = response.headers.get('X-Is-Old-Upload') === 'true';

        return { chunkSizes, encryptedFileString, isDeprecatedUpload, response };
      }),
      map((fileDownload) => {
        if (fileDownload.isDeprecatedUpload) {
          let uint8Array = this.getResultArray(fileSecret, fileDownload.chunkSizes, fileDownload.encryptedFileString);
          let file = new File([uint8Array.buffer], fileName, { type: fileType });
          this.loadFileForUser(file);
        } else {
          const file = new File([fileDownload.response.body], fileName, { type: fileType });
          this.loadFileForUser(file);
        }
      }),
      catchError(() => {
        return throwError(() => new Error('Failed to download file'));
      })
    );
  }

  private blobToText(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });
  }

  private getResultArray(fileSecret: string, chunkSizes: number[], encryptedFileString: string): Uint8Array {
    let simpleCrypto = new SimpleCrypto(fileSecret);
    let arrays: Uint8Array[] = [];
    let resultArrayLength: number = 0;

    let start = 0;
    let end = 0;
    for (const size of chunkSizes) {
      end = start + size;
      let chunk = encryptedFileString.slice(start, end);
      let decryptedChunkInBase64: PlainData = simpleCrypto.decrypt(chunk);
      if (typeof decryptedChunkInBase64 === 'string') {
        let array = this.mapBase64ToArrayBuffer(decryptedChunkInBase64);
        resultArrayLength = resultArrayLength + array.byteLength;
        arrays.push(array);
      }
      start += size;
    }

    let resultArray = new Uint8Array(resultArrayLength);
    let offset = 0;
    for (const array of arrays) {
      resultArray.set(array, offset);
      offset += array.length;
    }
    return resultArray;
  }

  private mapBase64ToArrayBuffer(base64String: string): Uint8Array {
    let arr = base64String.split(',');
    let bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    let u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return u8arr;
  }

  private loadFileForUser(file: File) {
    const url = window.URL.createObjectURL(file);
    window.open(url);
  }

  clearAll() {
    this.fileUploadStatus$.next([]);
    this.fileUploadStatus = [];
  }

  saveBinaryData(file: BinaryData) {
    this.http.post(this.fileUploadUrl + '/binary', file).subscribe();
  }

  public sendMembershipEmail(id: string, companyName: string, companyLocation: string) {
    this.http.post(this.fileUploadUrl + '/membership-email', { id, companyName, companyLocation }).subscribe({
      next: () => this.emailSent.emit()});
  }

  public sendExchangeVisitorEmail(id: string, companyName: string, exchangeVisitorName: string) {
    this.http.post(this.fileUploadUrl + '/exchange-visitor-email', { id, companyName, exchangeVisitorName }).subscribe({
      next: () => this.emailSent.emit()
    });
  }

  findBinaryData(): Observable<BinaryData[]> {
    return this.http.get<BinaryData[]>(this.fileUploadUrl + '/binary');
  }

  deleteBinaryData(id: string) {
    this.http.delete(this.fileUploadUrl + '/binary/' + id).subscribe();
  }

  deleteFileData(id: string) {
    this.http.delete(this.fileUploadUrl + '/' + id).subscribe();
  }

  public downloadExcelTemplate(): void {
    this.http.get(`${this.fileUploadUrl}/proposal-excel-template`, { responseType: 'blob' })
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = EXCEL_TEMPLATE_FILENAME;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          console.error('Failed to download Excel template:', err);
        }
      });
  }
}
